import { useEventListener } from "@/hooks/useEventBus";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = true,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  useEventListener("hotkey:enter", () => {
    if (open) onConfirm();
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent
        className="fixed max-w-[calc(100%-2.5rem)] gap-3 overflow-hidden rounded-[20px] border-white/[0.07] bg-[#17191d] p-4 text-white shadow-[0_24px_64px_rgba(0,0,0,0.38)] backdrop-blur-[24px] sm:max-w-[22rem]"
        overlayClassName="bg-[rgba(6,10,16,0.28)] backdrop-blur-[8px]"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">{message}</p>
        <DialogFooter className="!mx-0 !mb-0 mt-1 !flex-row !justify-end gap-1 !rounded-none border-t border-white/[0.07] !p-0 !pt-3">
          <Button
            className="!h-8 !w-auto !border-transparent !bg-transparent px-3 text-xs text-white/52 shadow-none hover:!bg-white/[0.04] hover:text-white"
            variant="outline"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            className={
              destructive
                ? "!h-8 !w-auto !border-transparent !bg-transparent px-3 text-xs text-rose-300 shadow-none hover:!bg-rose-400/10 hover:text-rose-200"
                : "!h-8 !w-auto !border-transparent !bg-transparent px-3 text-xs text-white/78 shadow-none hover:!bg-white/[0.05] hover:text-white"
            }
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
